// Phase 1A · Step 26/42 — /brief page (Phantom slip review).
//
// Step 42 correction (UnifiedShell adoption):
//   - The outer wrapper changed from <ModeShell mode="brief"> (Step 25)
//     to <UnifiedShell mode="brief"> (Step 37). The Brief now shares
//     the same institutional chrome as /home: a 36px context strip
//     with wordmark, safety state, and account dot, plus a 56px
//     right rail of five Adinkra-derived mode icons.
//   - The inner <BriefShell> (Step 26) is preserved unchanged. It
//     still provides the dossier framing — sidebar-free 640px
//     centered article with eyebrow + title + description + sub-nav
//     + back-link row. UnifiedShell's strip + rail and BriefShell's
//     dossier framing are at different layers and compose cleanly.
//   - <PhantomSlipPanel> behavior is identical — same hook, same
//     props, same RPC paths. No Phantom create/confirm/resolve/chain
//     contract changes.
//
// Composition only:
//   - <UnifiedShell mode="brief"> provides the institutional chrome.
//   - <BriefShell> provides the dossier framing.
//   - <PhantomSlipPanel> is reused as-is from src/pages/dashboard/.
//     The same component continues to render on /dashboard during
//     the migration window — server-side state via Supabase RLS-bounded
//     queries means concurrent surfaces stay consistent.
//
// `optedOut` derivation mirrors DashboardPage exactly. Same hooks,
// same predicate, same pass-through to the panel — no behavioural
// drift between dashboard and brief renderings.

import { useNavigate } from "react-router-dom";
import { usePhantomStatus } from "../../auth/usePhantomStatus.ts";
import { useUserProfile } from "../../auth/useUserProfile.ts";
import { BriefShell } from "../../modes/brief/BriefShell.tsx";
import { MODE_ROUTE_PREFIXES } from "../../modes/mode-config.ts";
import { getPhantomStatus } from "../../onboarding/profile-completion.ts";
import { UnifiedShell, type UnifiedMode } from "../../shell/index.ts";
import { PhantomSlipPanel } from "../dashboard/PhantomSlipPanel.tsx";

export function BriefPhantomPage(): JSX.Element {
  const navigate = useNavigate();
  const { profile } = useUserProfile();
  const { hasActiveSession } = usePhantomStatus();
  const phantomStatus = getPhantomStatus(profile, hasActiveSession);

  function handleNavigate(next: UnifiedMode): void {
    navigate(MODE_ROUTE_PREFIXES[next]);
  }

  return (
    <UnifiedShell
      mode="brief"
      contextEyebrow="THE BRIEF"
      contextTitle="Phantom slip dossier"
      safetyLabel="PHANTOM · SAFE MODE"
      accountLabel="1A"
      onNavigate={handleNavigate}
    >
      <BriefShell
        eyebrow="The Brief"
        title="Phantom slip review"
        description="Review, confirm, and resolve simulated Phantom slips. No real money is involved, no live match result is consulted, and no agent reasoning is active yet."
      >
        <PhantomSlipPanel optedOut={phantomStatus === "opted_out"} />
      </BriefShell>
    </UnifiedShell>
  );
}
